//
// Which mapping rules were left pointing at nothing when the code was saved,
// and which new function each of them probably meant.
//
// A mapping rule of type 'function' stores the NAME of a method. Rename the
// method and the rule still names the old one: it saves without complaint and
// then produces an error row per table row at conversion time instead of a
// value. This works out what to ask the user about.
//
// ⚠️ A rename cannot be proven. All that is visible is "one name gone, another
// one there". Comparing the bodies turns that into a good proposal — never into
// a fact, which is why nothing here writes anything. It produces questions.
//

export type TMethodSnapshot = {
    names: string[]
    /** name -> whitespace-normalised body, from getClassMethodBodies() */
    bodies: Record<string, string>
}

export type TRenameOffer = {
    /** _id of the mapping row, to save it back through the usual PATCH. */
    rowId: string
    /** Column key (c0..cn) — what the row calls its target. */
    target: string
    /** The target's readable name, for the dialog. */
    targetLabel: string
    /** The function the rule still points at, which no longer exists. */
    oldName: string
    /** The new function with an identical body, or '' when there is no such thing. */
    suggested: string
}

/**
 * An empty body matches every other empty body, so `{}` would suggest any newly
 * added stub for any deleted stub. A proposal that is right by coincidence is
 * worse than none: it is the one people click through.
 */
const isComparable = (body: string | undefined): body is string =>
    typeof body === 'string' && body.replace(/[{}\s]/g, '').length > 0

/**
 * @param before  the methods as they were when the editor was opened
 * @param after   the methods as they are now, after saving
 * @param rows    the mapping rows, as the editor holds them
 * @param label   turns a column key into the field name a person recognises
 */
export function buildRenameOffers(
    before: TMethodSnapshot,
    after: TMethodSnapshot,
    rows: any[],
    label: (target: string) => string
): TRenameOffer[] {
    const disappeared = before.names.filter((name) => !after.names.includes(name))
    if (disappeared.length === 0) return []

    const added = after.names.filter((name) => !before.names.includes(name))

    const suggestionFor = (oldName: string): string => {
        const oldBody = before.bodies[oldName]
        if (!isComparable(oldBody)) return ''

        // Exactly one match, or none. Two new functions with the same body say
        // nothing about which one inherited the name.
        const matches = added.filter((name) => after.bodies[name] === oldBody)
        return matches.length === 1 ? matches[0] : ''
    }

    // Only rules that actually point at a vanished function. A method deleted
    // while nothing referenced it is not the user's problem.
    const offers: TRenameOffer[] = []
    const suggestionCache = new Map<string, string>()

    for (const row of rows ?? []) {
        if (row?.type !== 'function') continue

        const oldName = String(row.source ?? '')
        if (!disappeared.includes(oldName)) continue

        if (!suggestionCache.has(oldName)) suggestionCache.set(oldName, suggestionFor(oldName))

        offers.push({
            rowId: String(row._id),
            target: String(row.target ?? ''),
            targetLabel: label(String(row.target ?? '')),
            oldName,
            suggested: suggestionCache.get(oldName) as string,
        })
    }

    return offers
}
